/* global sessionStorage */
import { useEffect, useState, useCallback } from "react"
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
  Dimensions,
  Platform,
  ScrollView,
  Linking,
} from "react-native"
import { useDispatch, useSelector } from "react-redux"
import TopGreenHeader from "../Common/TopGreenHeader"
import { useNavigation, useFocusEffect } from "@react-navigation/native"
import SelectClientModal from "./SelectClientModal"
import { getAllKidsCreator, getAllLocationCreator, getAllPlansCreator, loginActions } from "../../slices/authSlice"
import { getParentsListApi, generateParentTokenApi, getEventServicesApi } from "../../apiCalls/LoginApi"
import AsyncStorage from "@react-native-async-storage/async-storage"
import dayjs from "dayjs"
import { ToastContainer, toast } from "react-toastify"
import { DayPicker } from "react-day-picker"
import "react-day-picker/dist/style.css"
import DateTimePicker from "react-native-ui-datepicker"

const { width, height } = Dimensions.get("window")

// Display-only relabels (keyed by service type). The backend now sends the
// final descriptions ("Social Hub", "Breakfast Club"), so these only apply to
// legacy LEARNCARE / TRANSLEARN responses.
const SERVICE_LABELS = {
  LEARNCARE: "Social Hub",
  TRANSLEARN: "Breakfast Club",
}

// Synthetic Adhoc "Events" option — only pushed when the backend doesn't send
// an EVENT service itself (legacy responses). New responses include EVENT in
// Data.services, whose selection triggers the event-services fetch.
const EVENTS_OPTION = "Events"

// Fallback booking `type` + `description` per event package (keyed by `name`).
// The event-services API now sends `type` on each item, which takes precedence;
// this map only fills in missing fields on legacy responses.
const EVENT_SERVICE_TYPES = {
  "Starter DIY Package": { type: "EVENT-STARTER", description: "Events - Starter DIY Packages" },
  "Deluxe Custom Packages": { type: "EVENT-DELUX", description: "Events - Deluxe Custom Packages" },
  "Signature Curated Packages": { type: "EVENT-SIGNATURE", description: "Event - Signature Curated Packages" },
  "Party Helpers": { type: "EVENT-PARTY_HELPER", description: "Events- Party Helpers" },
}

const ScheduleServiceForm = () => {
  // Location and Service states
  const [location, setLocation] = useState("")
  const [locationsList, setLocationsList] = useState([])
  const [servicesList, setServicesList] = useState([])
  const [service, setService] = useState("")
  const [showLocationDropdown, setShowLocationDropdown] = useState(false)
  const [serviceDropdown, setServiceDropdown] = useState(false)

  // Event-services states (loaded when the "Events" service option is picked)
  const [eventServices, setEventServices] = useState([])
  const [loadingEventServices, setLoadingEventServices] = useState(false)
  const [selectedEventService, setSelectedEventService] = useState(null)
  const [eventServiceDropdown, setEventServiceDropdown] = useState(false)

  // Date and Time states
  const [date, setDate] = useState(new Date())
  const [formattedDate, setFormattedDate] = useState("")
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [startTime, setStartTime] = useState("")
  const [stopTime, setStopTime] = useState("")
  const [showStartTimePicker, setShowStartTimePicker] = useState(false)
  const [showStopTimePicker, setShowStopTimePicker] = useState(false)
  const [month, setMonth] = useState(new Date()) // Add this state to control the current month

  const [showSelectClientModal, setShowSelectClientModal] = useState(false)
  const [storedClientName, setStoredClientName] = useState(null)
  const [parentsList, setParentsList] = useState([])
  const [loadingParents, setLoadingParents] = useState(false)

  useEffect(() => {
    const initAdmin = async () => {
      try {
        const userType = await AsyncStorage.getItem("userType");
        if (userType === `"admin"` || userType === "admin") {
          const clientId = await AsyncStorage.getItem("clientId");
          const clientToken = await AsyncStorage.getItem("clientToken");
          const clientName = await AsyncStorage.getItem("clientName");
          if (clientId && clientToken && clientName) {
            setStoredClientName(clientName);
          } else {
            setShowSelectClientModal(true);
            setLoadingParents(true);
            const res = await getParentsListApi();
            if (res?.Success === 1 && res.Data?.parents) {
              setParentsList(res.Data.parents);
            }
            setLoadingParents(false);
          }
        }
      } catch (e) {
        setLoadingParents(false);
        console.error(e);
      }
    };
    initAdmin();
  }, []);

  const handleUserPress = async () => {
    try {
      await AsyncStorage.removeItem("clientId");
      await AsyncStorage.removeItem("clientToken");
      await AsyncStorage.removeItem("clientName");
      const userType = await AsyncStorage.getItem("userType");
      if (userType === `"admin"` || userType === "admin") {
        setShowSelectClientModal(true);
        if (parentsList.length === 0) {
          setLoadingParents(true);
          const res = await getParentsListApi();
          if (res?.Success === 1 && res.Data?.parents) {
            setParentsList(res.Data.parents);
          }
          setLoadingParents(false);
        }
      }
    } catch (e) {
      setLoadingParents(false);
      console.error(e);
    }
  };

  const handleSelectClient = async (client) => {
    setStoredClientName(client.parentName);

    try {
      const res = await generateParentTokenApi({ userId: client.parentId });
      if (res?.Success === 1 && res.Data) {
        await AsyncStorage.setItem('clientId', res.Data?.user?.userId);
        await AsyncStorage.setItem('clientName', res.Data?.user?.name);
        await AsyncStorage.setItem('clientToken', res.Data.token);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Other states and redux/navigation
  const [activeTab, setActiveTab] = useState("Adhoc")
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const getLocation = useSelector((state) => state.auth.getgetAllLocation)

  useEffect(() => {
    dispatch(getAllLocationCreator())
  }, [dispatch])

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const hasReloaded = sessionStorage.getItem('hasReloadedScheduleService');
        if (!hasReloaded) {
          sessionStorage.setItem('hasReloadedScheduleService', 'true');
          window.location.reload();
        }
      }

      return () => {
        if (Platform.OS === 'web') {
          sessionStorage.removeItem('hasReloadedScheduleService');
        }
      };
    }, [])
  )

  useEffect(() => {
    if (getLocation?.Data) {
      const { locations, services } = getLocation.Data

      if (locations?.length > 0) {
        setLocationsList(locations.map((item) => item?.name))
      }

      if (services?.length > 0) {
        let filteredServices = services.map((service) => service?.description)

        if (activeTab === "Adhoc") {
          // Adhoc offers Home Care + Social Hub + Transportation + Events. The backend now sends
          // SOCIALHUB / EVENT types directly (LEARNCARE kept for legacy responses).
          filteredServices = services
            .filter((service) =>
              ["HOMECARE", "SOCIALHUB", "LEARNCARE", "TRANSPORT", "EVENT"].includes(service?.type),
            )
            .map((service) => service.description)
          if (!services.some((service) => service?.type === "EVENT")) {
            filteredServices.push(EVENTS_OPTION)
          }
        } else if (activeTab === "Recurring") {
          filteredServices = services
            .filter((service) =>
              ["SOCIALHUB", "TRANSPORT", "BREAKFASTCLUB", "LEARNCARE", "TRANSLEARN"].includes(service?.type),
            )
            .map((service) => service.description)
        }

        setServicesList(filteredServices)
      }
    }
  }, [getLocation, activeTab])

  // Resolves a service description back to its backend type code.
  const getServiceType = (description) =>
    getLocation?.Data?.services?.find((item) => item?.description === description)?.type

  // The Events flow is entered either via the backend EVENT service or the
  // legacy synthetic "Events" option.
  const isEventsService = (description) =>
    description === EVENTS_OPTION || getServiceType(description) === "EVENT"

  const requiresStartStopTime = (serviceType) => {
    return serviceType === "SOCIALHUB" || serviceType === "LEARNCARE" || serviceType === "HOMECARE"
  }

  const switchTabsHandleClick = (tabname) => {
    setActiveTab(tabname)
    setService("")
    setFormattedDate("")
    setStartTime("")
    setStopTime("")
    setSelectedEventService(null)
    setEventServices([])
    dispatch(loginActions.setTab(tabname))
    setLocation(locationsList[0])
  }

  const handleNext = () => {
    const notify = (message) => toast.error(`Please enter ${message}`)
    if (!service) {
      notify("service")
    } else if (isEventsService(service) && !selectedEventService) {
      notify("Event Service")
    } else if (!location) {
      notify("Location")
    } else if (!formattedDate) {
      notify("Date")
    } else {
      // For "Events" the type/description come from the selected event package;
      // otherwise resolve them from the location's service list.
      let serviceName
      let serviceDescription
      if (isEventsService(service)) {
        serviceName = selectedEventService?.type || null
        serviceDescription = selectedEventService?.description || selectedEventService?.name
      } else {
        const result = getLocation?.Data?.services.find((item) => item.description === service)
        serviceName = result ? result.type : null
        serviceDescription = service
      }

      if (activeTab === "Adhoc") {
        if (requiresStartStopTime(serviceName)) {
          if (!startTime) {
            notify("Start Time")
            return
          }
          if (!stopTime) {
            notify("Stop Time")
            return
          }
          const convertTimeToMinutes = (timeStr) => {
            const [hours, minutes] = timeStr.split(":").map(Number)
            return hours * 60 + minutes
          }

          const startTimeMinutes = convertTimeToMinutes(startTime)
          const stopTimeMinutes = convertTimeToMinutes(stopTime)

          if (stopTimeMinutes <= startTimeMinutes) {
            toast.error("Stop time must be after start time")
            return
          }
        } else if (!startTime) {
          notify("Time")
          return
        }
      }

      dispatch(getAllKidsCreator())
      dispatch(loginActions.setTab(activeTab))
      dispatch(
        loginActions.setScheduledService({
          location,
          service: serviceDescription,
          formattedDate,
          formattedTime:
            activeTab === "Adhoc" && requiresStartStopTime(serviceName) ? `${startTime} - ${stopTime}` : startTime,
          serviceName,
          bookingType: activeTab,
        }),
      )
      navigation.navigate("SelectKidScreen")
    }
  }

  const handleSeeAllPlans = () => {
    const notify = (message) => toast.error(`Please enter ${message}`)
    if (!service) {
      notify("service")
      return
    } else if (!location) {
      notify("Location")
      return
    } else if (!formattedDate) {
      notify("Date")
      return
    }

    const result = getLocation?.Data?.services.find((item) => item.description === service)
    const serviceName = result ? result.type : null

    dispatch(getAllPlansCreator())
    dispatch(loginActions.setTab(activeTab))
    dispatch(
      loginActions.setScheduledService({
        location,
        service,
        formattedDate,
        formattedTime:
          activeTab === "Adhoc" && requiresStartStopTime(serviceName) ? `${startTime} - ${stopTime}` : startTime,
        serviceName,
        bookingType: activeTab,
      }),
    )

    // Social Hub skips the plans screen — plans are auto-matched per day by kid count.
    if (serviceName === "SOCIALHUB" || serviceName === "LEARNCARE") {
      dispatch(loginActions.setplanChoosed({
        planService: 'SOCIALHUB',
        isFixedWeekdays: false,
      }))
      navigation.navigate("SelectDaysScreen")
    } else {
      navigation.navigate("SeeAllPlans")
    }
  }

  const seePlansList = () => {
    Linking.openURL("https://danilees.s3.us-east-2.amazonaws.com/DanileesPlan.pdf")
      .catch(err => console.error("Failed to open URL:", err));
  }

  const handleLocationSelect = (item) => {
    setLocation(item)
    setShowLocationDropdown(false)
  }

  const handleServiceDropdown = (item) => {
    setService(item)
    setServiceDropdown(false)

    if (isEventsService(item)) {
      setSelectedEventService(null)
      fetchEventServices()
    }
  }

  const fetchEventServices = async () => {
    try {
      setLoadingEventServices(true)
      const res = await getEventServicesApi()
      if (res?.Success === 1 && Array.isArray(res.Data)) {
        // The API now returns the booking `type` on each package; the local map
        // only backfills type/description when the response omits them.
        const enriched = res.Data.map((item) => ({
          ...(EVENT_SERVICE_TYPES[item?.name] || {}),
          ...item,
        }))
        setEventServices(enriched)
      } else {
        setEventServices([])
        toast.error(res?.Message || "Failed to load event services")
      }
    } catch (e) {
      console.error(e)
      setEventServices([])
      toast.error("Failed to load event services")
    } finally {
      setLoadingEventServices(false)
    }
  }

  const handleEventServiceSelect = (item) => {
    setSelectedEventService(item)
    setEventServiceDropdown(false)
  }

  // Maps a stored service description to its display label
  const getServiceLabel = (description) => {
    if (!description) return description
    const matched = getLocation?.Data?.services.find((item) => item?.description === description)
    return SERVICE_LABELS[matched?.type] || description
  }

  const formatTo12Hour = (time) => {
    if (!time) return ""
    const [h, m] = time.split(":").map(Number)
    const suffix = h >= 12 ? "PM" : "AM"
    const hour12 = h % 12 === 0 ? 12 : h % 12
    return `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`
  }

  // Date/Time change handlers
  const handleDateChange = (selectedDate) => {
    if (selectedDate) {
      setDate(selectedDate)
      setFormattedDate(dayjs(selectedDate).format("DD/MM/YYYY"))
      setStartTime("")
      setStopTime("")
      setShowDatePicker(false)
    }
  }

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth)
  }

  const handleStartTimeChange = (selectedTime) => {
    setStartTime(selectedTime)
    setShowStartTimePicker(false)
  }

  const handleStopTimeChange = (selectedTime) => {
    const convertTimeToMinutes = (timeStr) => {
      if (!timeStr) return 0
      const [hours, minutes] = timeStr.split(":").map(Number)
      return hours * 60 + minutes
    }

    const startTimeMinutes = convertTimeToMinutes(startTime)
    const selectedTimeMinutes = convertTimeToMinutes(selectedTime)

    if (!startTime || selectedTimeMinutes > startTimeMinutes) {
      setStopTime(selectedTime)
      setShowStopTimePicker(false)
    } else {
      toast.error("Stop time must be after start time")
      setShowStopTimePicker(false)
    }
  }

  const switchTabs = () => (
    <View style={styles.tabContainer}>
      <TouchableOpacity
        style={[styles.tab, activeTab === "Adhoc" && styles.activeTab]}
        onPress={() => switchTabsHandleClick("Adhoc")}
      >
        <Text style={[styles.tabText, activeTab === "Adhoc" && styles.activeTabText]}>Adhoc</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.tab, activeTab === "Recurring" && styles.activeTab]}
        onPress={() => switchTabsHandleClick("Recurring")}
      >
        <Text style={[styles.tabText, activeTab === "Recurring" && styles.activeTabText]}>Recurring</Text>
      </TouchableOpacity>
    </View>
  )

  const lOcationModal = () => (
    <Modal transparent visible={showLocationDropdown} onRequestClose={() => setShowLocationDropdown(false)}>
      <TouchableOpacity style={styles.modalOverlay} onPress={() => setShowLocationDropdown(false)} />
      <View style={styles.dropdownContainer}>
        <FlatList
          data={locationsList}
          keyExtractor={(item, index) => index.toString()}
          renderItem={({ item }) => (
            <TouchableOpacity style={styles.dropdownItem} onPress={() => handleLocationSelect(item)}>
              <Text>{item}</Text>
            </TouchableOpacity>
          )}
        />
      </View>
    </Modal>
  )

  const serviceDropdownModal = () => (
    <Modal transparent visible={serviceDropdown} onRequestClose={() => setServiceDropdown(false)}>
      <TouchableOpacity style={styles.modalOverlay} onPress={() => setServiceDropdown(false)} />
      <View style={styles.dropdownContainer}>
        <FlatList
          data={servicesList}
          keyExtractor={(item, index) => index.toString()}
          renderItem={({ item }) => (
            <TouchableOpacity style={styles.dropdownItem} onPress={() => handleServiceDropdown(item)}>
              <Text>{getServiceLabel(item)}</Text>
            </TouchableOpacity>
          )}
        />
      </View>
    </Modal>
  )

  const eventServiceDropdownModal = () => (
    <Modal transparent visible={eventServiceDropdown} onRequestClose={() => setEventServiceDropdown(false)}>
      <TouchableOpacity style={styles.modalOverlay} onPress={() => setEventServiceDropdown(false)} />
      <View style={styles.dropdownContainer}>
        <FlatList
          data={eventServices}
          keyExtractor={(item, index) => index.toString()}
          renderItem={({ item }) => (
            <TouchableOpacity style={styles.dropdownItem} onPress={() => handleEventServiceSelect(item)}>
              <View style={styles.eventServiceRow}>
                <Text>{item?.name}</Text>
                <Text style={styles.eventServiceAmount}>{item?.amount}</Text>
              </View>
            </TouchableOpacity>
          )}
        />
      </View>
    </Modal>
  )

  const datePickerModal = () => (
    <Modal transparent visible={showDatePicker} onRequestClose={() => setShowDatePicker(false)}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowDatePicker(false)}>
        <View style={styles.calendarWrapper} onClick={(e) => e.stopPropagation()}>
          <View style={styles.calendarContainer1} onClick={(e) => e.stopPropagation()}>
            {Platform.OS !== 'web' ? (
              <DateTimePicker
                mode="single"
                date={date}
                calendarTextStyle={{ color: '#505766' }}
                headerTextStyle={{ color: '#505766' }}
                weekDaysTextStyle={{ color: '#505766' }}
                minDate={dayjs().startOf('day').toDate()}
                onChange={({ date: selected }) => handleDateChange(selected)}
              />
            ) : (
              <DayPicker
                mode="single"
                required
                selected={date}
                onSelect={handleDateChange}
                month={month}
                onMonthChange={handleMonthChange}
                disabled={[{ before: dayjs().startOf("day").toDate() }]}
                modifiersStyles={{
                  selected: {
                    backgroundColor: "#8dc63f",
                    color: "white",
                    fontWeight: "600",
                  },
                  today: {
                    color: "#8dc63f",
                    fontWeight: "600",
                  },
                }}
                styles={{
                  root: { margin: 0 },
                  months: { margin: 0 },
                  month: { margin: 0 },
                  caption: { display: "none" }, // Hide the default caption
                  table: { margin: "0 auto", width: "100%" },
                  head_row: { height: 32, color: "#666" },
                  row: { width: "100%" },
                  head_cell: { padding: "4px 0", fontSize: "0.875rem", fontWeight: "500", color: "#666" },
                  cell: { padding: "2px 0" },
                  day: { margin: "1px", height: 36, width: 36, fontSize: "0.875rem", borderRadius: "50%" },
                  day_selected: { backgroundColor: "#8dc63f", color: "white", fontWeight: "600" },
                  day_today: { color: "#8dc63f", fontWeight: "600" },
                  day_outside: { opacity: 0.5 },
                }}
              />
            )}
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  )

  const timePickerModal = (isStartTime) => (
    <Modal
      transparent
      visible={isStartTime ? showStartTimePicker : showStopTimePicker}
      onRequestClose={() => (isStartTime ? setShowStartTimePicker(false) : setShowStopTimePicker(false))}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        onPress={() => (isStartTime ? setShowStartTimePicker(false) : setShowStopTimePicker(false))}
      />
      <View style={styles.dropdownContainer}>
        <FlatList
          data={Array.from({ length: 24 * 4 }, (_, i) => {
            const hours = Math.floor(i / 4)
            const minutes = (i % 4) * 15
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
          }).filter((time) => {
            const [timeHour, timeMinute] = time.split(":").map(Number)
            const timeTotalMinutes = timeHour * 60 + timeMinute

            // Check if selected date is today
            const isToday =
              date &&
              date.getDate() === new Date().getDate() &&
              date.getMonth() === new Date().getMonth() &&
              date.getFullYear() === new Date().getFullYear()

            if (isToday) {
              const currentHour = new Date().getHours()
              const currentMinute = new Date().getMinutes()
              const currentTotalMinutes = currentHour * 60 + currentMinute

              // Add a 2-hour (120 minutes) buffer to the current time
              if (timeTotalMinutes <= currentTotalMinutes + 120) return false
            }

            // If this is the stop time picker, only show times after the start time
            if (!isStartTime && startTime) {
              const [startHour, startMinute] = startTime.split(":").map(Number)
              const startTotalMinutes = startHour * 60 + startMinute

              return timeTotalMinutes > startTotalMinutes
            }

            return true
          })}
          keyExtractor={(item) => item}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={() => {
                if (isStartTime) {
                  handleStartTimeChange(item)
                } else {
                  handleStopTimeChange(item)
                }
              }}
            >
              <Text>{formatTo12Hour(item)}</Text>
            </TouchableOpacity>
          )}
        />
      </View>
    </Modal>
  )

  return (
    <>
      <ScrollView style={styles.scrollViewContainer}>
        <TopGreenHeader
          Heading={"Schedule"}
          navigation={navigation}
          isBackIcon={false}
          client={storedClientName}
          isNotification={true}
          onUserPress={handleUserPress}
        />
        <View style={styles.container}>
          <Text style={styles.title}>Schedule a Service</Text>
          {switchTabs()}
          <View style={styles.formContainer}>
            <TouchableOpacity style={styles.input} onPress={() => setShowLocationDropdown(true)}>
              <Text style={{ color: location ? "#000" : "#aaa", paddingVertical: 15 }}>
                {location || "Select Location"}
              </Text>
            </TouchableOpacity>
            {lOcationModal()}

            <TouchableOpacity style={styles.input} onPress={() => setServiceDropdown(true)}>
              <Text style={{ color: service ? "#000" : "#aaa", paddingVertical: 15 }}>
                {getServiceLabel(service) || "Select Service"}
              </Text>
            </TouchableOpacity>
            {serviceDropdownModal()}

            {isEventsService(service) && (
              <>
                <TouchableOpacity
                  style={styles.input}
                  onPress={() => setEventServiceDropdown(true)}
                  disabled={loadingEventServices}
                >
                  <Text style={{ color: selectedEventService ? "#000" : "#aaa", paddingVertical: 15 }}>
                    {loadingEventServices
                      ? "Loading event services..."
                      : selectedEventService?.name || "Select Event Service"}
                  </Text>
                </TouchableOpacity>
                {eventServiceDropdownModal()}
              </>
            )}

            <TouchableOpacity style={styles.input} onPress={() => setShowDatePicker(true)}>
              <Text style={{ color: formattedDate ? "#000" : "#aaa", paddingVertical: 15 }}>
                {formattedDate || "Select Date"}
              </Text>
            </TouchableOpacity>
            {datePickerModal()}

            {activeTab === "Adhoc" && (
              <>
                <TouchableOpacity style={styles.input} onPress={() => setShowStartTimePicker(true)}>
                  <Text style={{ color: startTime ? "#000" : "#aaa", paddingVertical: 15 }}>
                    {formatTo12Hour(startTime) || "Select Start Time"}
                  </Text>
                </TouchableOpacity>
                {timePickerModal(true)}

                {requiresStartStopTime(
                  getLocation?.Data?.services.find((item) => item.description === service)?.type,
                ) && (
                    <>
                      <TouchableOpacity style={styles.input} onPress={() => setShowStopTimePicker(true)}>
                        <Text style={{ color: stopTime ? "#000" : "#aaa", paddingVertical: 15 }}>
                          {formatTo12Hour(stopTime) || "Select Stop Time"}
                        </Text>
                      </TouchableOpacity>
                      {timePickerModal(false)}
                    </>
                  )}
              </>
            )}

            <TouchableOpacity
              style={styles.button}
              onPress={activeTab === "Recurring" ? handleSeeAllPlans : handleNext}
            >
              <Text style={styles.buttonText}>Next</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.button} onPress={seePlansList}>
              <Text style={styles.buttonText}>See All Plans</Text>
            </TouchableOpacity>

            <TouchableOpacity style={[styles.button, styles.bookEventButton]} onPress={() => navigation.navigate('ProfileEventsList')}>
              <Text style={styles.buttonText}>Camps</Text>
            </TouchableOpacity>
          </View>
        </View>
        <ToastContainer />
        <SelectClientModal
          visible={showSelectClientModal}
          onClose={() => setShowSelectClientModal(false)}
          onSelectClient={handleSelectClient}
          clients={parentsList}
          loading={loadingParents}
        />
      </ScrollView>

      {Platform.OS === 'web' && showDatePicker && (
        <style jsx global>{`
          .rdp {
            --rdp-cell-size: 36px;
            --rdp-accent-color: #8dc63f;
            --rdp-background-color: #e7f5d4;
            margin: 0;
            padding: 0;
          }
          .rdp-months {
            margin: 0;
            padding: 0;
          }
          .rdp-month {
            margin: 0;
            padding: 0;
          }
          .rdp-table {
            margin: 0;
            width: 100%;
          }
          .rdp-cell {
            padding: 0;
            text-align: center;
          }
          .rdp-head_cell {
            font-weight: 500;
            font-size: 0.875rem;
            color: #666;
            padding: 4px 0;
          }
          .rdp-day {
            margin: 1px;
            height: 36px;
            width: 36px;
            font-size: 0.875rem;
            border-radius: 50%;
          }
          .rdp-day_selected {
            background-color: #8dc63f !important;
            color: white;
            font-weight: 600;
          }
          .rdp-day_today {
            color: #8dc63f;
            font-weight: 600;
          }
          .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
            background-color: #e7f5d4;
          }
        `}</style>
      )}
    </>
  )
}

const styles = StyleSheet.create({
  scrollViewContainer: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  container: {
    flex: 1,
    alignItems: "center",
    padding: 15,
    backgroundColor: "#ffffff",
    position: "relative",
  },
  title: {
    fontSize: 20,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
    marginBottom: 10,
    color: "#000000",
  },
  formContainer: {
    width: "100%",
    maxWidth: 500,
    alignItems: "center",
    backgroundColor: "#ffffff",
  },
  input: {
    width: "100%",
    height: 50,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
    borderWidth: 1,
    borderColor: "#cccccc",
    borderRadius: 10,
    paddingHorizontal: 15,
    marginBottom: 15,
    fontSize: 16,
    backgroundColor: "#ffffff",
    justifyContent: "center",
  },
  button: {
    width: "100%",
    height: 50,
    backgroundColor: "#8dc63f",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10,
    marginTop: 15,
  },
  buttonText: {
    fontSize: 18,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
    color: "#ffffff",
  },
  bookEventButton: {
    backgroundColor: '#E94D8B',
  },
  eventServiceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  eventServiceAmount: {
    color: "#E94D8B",
    fontWeight: "bold",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  dropdownContainer: {
    position: "absolute",
    top: height * 0.25,
    left: width * 0.05,
    right: width * 0.05,
    backgroundColor: "#ffffff",
    borderRadius: 10,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
    borderWidth: 1,
    borderColor: "#ddd",
    maxHeight: height * 0.5,
    zIndex: 1,
  },
  dropdownItem: {
    padding: 15,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    backgroundColor: "#ffffff",
  },
  tabContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
    paddingVertical: 10,
    paddingHorizontal: 20,
    width: "100%",
    marginBottom: 20,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 10,
    backgroundColor: "#ffffff",
    borderRadius: 5,
    marginHorizontal: 5,
  },
  activeTab: {
    backgroundColor: "#A6CE39",
  },
  tabText: {
    fontSize: 18,
    color: "#979191",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
  activeTabText: {
    color: "#fff",
  },
  calendarWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  calendarContainer1: {
    width: "100%",
    maxWidth: 320,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  navButtons: {
    position: "absolute",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  navButton: {
    padding: 8,
  },
  navButtonText: {
    fontSize: 18,
    color: "#666",
  },
})

export default ScheduleServiceForm

